import React, { useState } from "react";
import { Box, Button, Tooltip, IconButton, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useOutletContext } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ConfirmationDialog from "../../../ConfirmationDialog";
import {
  clearSquareIntegration,
  fetchSquareAuthUrl,
  refreshSquareLocations,
  squareLocationsSelector,
} from "../../../../redux/features/organizationsSlice";

const SquareAuthUrl = ({ handleRequestUrl }) => {
  return (
    <Box
      marginTop={"20px"}
      display={"flex"}
      flexDirection={"column"}
      alignContent={"right"}
    >
      <Typography>
        Let's get you set up so we can start ticketing and put money into your
        Square account.
      </Typography>
      <Button
        sx={{ width: "300px", marginTop: "20px" }}
        variant="contained"
        onClick={() => {
          handleRequestUrl();
        }}
      >
        Authorize Comedy Ticketing
      </Button>
    </Box>
  );
};

const SquareIntegration = ({
  handleRequestUrl,
  organization,
  handleClearIntegration,
  handleRefreshLocations,
  squareLocations,
}) => {
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const { squareIntegrationSuccess } = organization;
  return (
    <Box>
      <ConfirmationDialog
        open={confirmationDialogOpen}
        title={"Remove Square Integration?"}
        message={
          "Removing the Square integration will prevent ticket sales for all events. Do you want to proceed?"
        }
        handleCancel={() => setConfirmationDialogOpen(false)}
        handleConfirm={async () => {
          await handleClearIntegration();
          setConfirmationDialogOpen(false);
        }}
      />
      <Typography variant="h4">Square Integration</Typography>
      {squareIntegrationSuccess ? (
        <Box marginTop={"20px"}>
          <Box display={"flex"} gap={"20px"} alignItems={"center"}>
            <CheckIcon color="primary" />
            <Typography>Square has been successfully integrated</Typography>
            <Tooltip title="Remove Square Integration">
              <IconButton onClick={() => setConfirmationDialogOpen(true)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box display={"flex"}>
            <Typography variant="h6" marginTop={"20px"} marginBottom={"20px"}>
              Payment Locations
            </Typography>
            <Tooltip title="Refresh Square Locations">
              <IconButton onClick={() => handleRefreshLocations()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <SquareLocationList squareLocations={squareLocations} />
        </Box>
      ) : (
        <SquareAuthUrl handleRequestUrl={handleRequestUrl} />
      )}
      {/* <SquareKeyEntry
          handleSaveSquareAccessToken={handleSaveSquareAccessToken}
        /> */}
    </Box>
  );
};

const SquareLocationList = ({ squareLocations }) => {
  return (
    <Box>
      {squareLocations?.map((squareLocation) => {
        const { name, address, id, status, capabilities } = squareLocation;
        const {
          addressLine1,
          addressLine2,
          administrativeDistrictLevel1,
          locality,
          postalCode,
        } = address || {};
        const isActive = status === "ACTIVE";
        const canProcessCreditCards =
          capabilities &&
          Array.isArray(capabilities) &&
          capabilities.includes("CREDIT_CARD_PROCESSING");
        return (
          <Box key={`location-${id}`} marginBottom={"16px"}>
            <Box
              display={"flex"}
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={"12px"}
            >
              <Typography>{name}</Typography>
              <Box
                display={"flex"}
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={"12px"}
                flexWrap="wrap"
              >
                {isActive ? (
                  <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="body2" color="success.main">
                      Active
                    </Typography>
                  </Box>
                ) : (
                  <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                    <ErrorIcon color="error" fontSize="small" />
                    <Typography variant="body2" color="error.main">
                      {status || "UNKNOWN"}
                    </Typography>
                  </Box>
                )}
                {canProcessCreditCards ? (
                  <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="body2" color="success.main">
                      Able to process credit cards
                    </Typography>
                  </Box>
                ) : (
                  <Tooltip
                    title="This location cannot process credit card payments. Please enable credit card processing for this location in your Square account settings."
                    arrow
                  >
                    <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                      <ErrorIcon color="error" fontSize="small" />
                      <Typography variant="body2" color="error.main">
                        Unable to process credit cards
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Box>
            {address && (
              <Typography color={"primary.lightText"}>
                {`${addressLine1}${
                  addressLine2 ? " " + addressLine2 : ""
                }, ${locality}, ${administrativeDistrictLevel1}, ${postalCode}`}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const Payments = () => {
  const { organization } = useOutletContext();
  const dispatch = useDispatch();
  const allSquareLocations = useSelector(squareLocationsSelector);

  const { id: organizationId } = organization;
  const squareLocations = allSquareLocations[organizationId] || [];

  const handleRequestUrl = async () => {
    const url = await dispatch(fetchSquareAuthUrl({ organizationId }));
    window.location = url;
  };

  const handleClearIntegration = async () => {
    dispatch(clearSquareIntegration({ organizationId }));
  };

  const handleRefreshLocations = async () => {
    dispatch(refreshSquareLocations({ organizationId }));
  };

  return (
    <Box marginTop={"20px"}>
      <Box sx={{ marginTop: "20px" }}>
        <SquareIntegration
          handleRequestUrl={handleRequestUrl}
          organization={organization}
          handleClearIntegration={handleClearIntegration}
          handleRefreshLocations={handleRefreshLocations}
          squareLocations={squareLocations}
        />
      </Box>
    </Box>
  );
};

export default Payments;
